import { Box, Container, Heading, Text, VStack, Grid } from "@chakra-ui/react";
import { Helmet } from 'react-helmet-async';
import ServiceDetailsHeader from "../../components/Common/ImageHeader";
import ServiceCard from "../../components/Common/ServiceCard";
import CaseStudySection from "../../components/Common/CaseStudySection";
import ServicesGrid from "../../components/Common/ServicesGrid";
import ExploreSection from "../../components/ProductDetails/ExploreSection";

const RecordDigitizationServicesPage = () => {
  // Statistics Data
  const statisticsData = [
    {
      id: 1,
      number: "64,000+",
      title: "Data Entries",
      icon: "/svg/dr_1.svg",
    },
    {
      id: 2,
      number: "7 Million+",
      title: "Image Scanning",
      icon: "/svg/dr_2.svg",
    },
    {
      id: 3,
      number: "5 Million+",
      title: "Page Scanning",
      icon: "/svg/dr_3.svg",
    },
    {
      id: 4,
      number: "50,000+",
      title: "Map Scanning",
      icon: "/svg/dr_4.svg",
    },
  ];

  // Specialized Services Data
  const specializedServices1 = [
    {
      id: 1,
      title: "Batch scanning of millions of documents",
      description: "Legal, A4, Maps, Photos",
      icon: "/svg/dev_1.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Intelligent QC and image enhancement",
      description: "Advanced quality control and optimization",
      icon: "/svg/dev_2.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 3,
      title: "Complex digitization jobs",
      description: "Architectural Design Image processing",
      icon: "/svg/dev_3.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
  ];
  const specializedServices2 = [
    {
      id: 1,
      title: "Context-based keyword tagging",
      description: "Intelligent content categorization",
      icon: "/svg/dev_4.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 2,
      title: "Custom metadata for fast retrieval",
      description: "Optimized search and access systems",
      icon: "/svg/dev_5.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
    {
      id: 3,
      title: "OCR-based indexing for searchability",
      description: "Text recognition and indexing",
      icon: "/svg/dev_6.svg",
      bgColor: "blue.50",
      iconColor: "#000B8C",
    },
  ];

  // System Integration Features Data
  const systemIntegrationFeatures = [
    {
      id: 1,
      title: "eGovernment portals & web apps",
      icon: "/svg/dev_7.svg",
    },
    {
      id: 2,
      title: "Document Management Systems (DMS)",
      icon: "/svg/dev_8.svg",
    },
    {
      id: 3,
      title: "Workflow automation platforms",
      icon: "/svg/dev_9.svg",
    },
    {
      id: 4,
      title: "CRM and ERP systems",
      icon: "/svg/dev_10.svg",
    },
  ];

  // Tools & Technologies Data for ServicesGrid
  const toolsAndTechnologies = [
    {
      id: 1,
      title: "Industrial-grade Fujitsu & Kodak scanners",
      icon: "/svg/support_service_1.svg",
      isTopRow: true,
      isLeftmost: true,
    },
    {
      id: 2,
      title: "OCR & ICR software (e.g., ABBYY, Adobe)",
      icon: "/svg/support_service_2.svg",
      isTopRow: true,
    },
    {
      id: 3,
      title: "Metadata extraction using AI-based tagging",
      icon: "/svg/support_service_3.svg",
      isTopRow: true,
      isRightmost: true,
    },
    {
      id: 4,
      title: "Digital archiving systems with cloud or on-prem deployment",
      icon: "/svg/support_service_4.svg",
      isBottomRow: true,
      isLeftmost: true,
    },
    {
      id: 5,
      title: "RESTful APIs for system integration",
      icon: "/svg/support_service_5.svg",
      isBottomRow: true,
    },
    {
      id: 6,
      title: "Blockchain-ready document logs (on request)",
      icon: "/svg/support_service_6.svg",
      isBottomRow: true,
      isRightmost: true,
    },
  ];
  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>Record Digitization Services | Data entry, Scanning, Archiving & AI-Ready Document Management in Bangladesh</title>
        <meta 
          name="description" 
          content="Modernize your archives with Business Automation's end-to-end digitization services—data entry, record & map scanning, OCR indexing, secure archiving, AI-powered retrieval, and seamless integration into e-governance or enterprise systems." 
        />
        <meta 
          name="keywords" 
          content="Record digitization services Bangladesh, Document scanning Bangladesh, Digital archive service Bangladesh, OCR indexing Bangladesh, Government record digitization, Secure document management Bangladesh, AI-powered document processing, citizen service document digitization, investment record archive Bangladesh, workflow digitization Bangladesh, e-governance digital record system, Data entry and scanning services, Map scanning" 
        />
        <link rel="canonical" href="https://www.ba-systems.com/services/record-digitization-services" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content="https://ba-systems.com/services/record-digitization-services" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Record Digitization Services | Data entry, Scanning, Archiving & AI-Ready Document Management in Bangladesh" />
        <meta property="og:description" content="Modernize your archives with Business Automation's end-to-end digitization services—data entry, record & map scanning, OCR indexing, secure archiving, AI-powered retrieval, and seamless integration into e-governance or enterprise systems." />
        <meta property="og:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="ba-systems.com" />
        <meta property="twitter:url" content="https://ba-systems.com/services/record-digitization-services" />
        <meta name="twitter:title" content="Record Digitization Services | Data entry, Scanning, Archiving & AI-Ready Document Management in Bangladesh" />
        <meta name="twitter:description" content="Modernize your archives with Business Automation's end-to-end digitization services—data entry, record & map scanning, OCR indexing, secure archiving, AI-powered retrieval, and seamless integration into e-governance or enterprise systems." />
        <meta name="twitter:image" content="https://opengraph.b-cdn.net/production/images/e1930f8a-3aa2-4fe1-b6ea-0ad5030ebc97.png?token=vTkCXbq7qBnee_uxOFTj3-OKo7vP2KVzX66L6BYKzho&height=596&width=1200&expires=33297038875" />
      </Helmet>
      
      <ServiceDetailsHeader
        title="Record Digitization Services"
        subtitle="Business Automation Limited's Contribution to the Digitization of Government Documents"
        backgroundImage="/png/bg_digi.png"
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/our-services" },
          { label: "Record Digitization Services", href: "#", isCurrentPage: true },
        ]}
      />

      <Box
        py={{ base: "30px", md: "40px" }}
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          minHeight: "100vh",
        }}
      >
        <Container maxW="7xl">
          <VStack spacing={8} align="start">
            {/* Header Section */}
            <VStack spacing={4} align="start" w="full">
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                lineHeight="1.2"
                textAlign="justify"
              >
                In Bangladesh, most government offices have long operated
                through manual processes, making it time-consuming and difficult
                to retrieve important documents when needed. To address this
                challenge, Business Automation Limited has developed a modern
                archiving software that digitizes paper documents through
                scanning and enables easy access for authorized officials.
              </Text>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="gray.600"
                lineHeight="1.2"
                textAlign="justify"
              >
                This software is already being successfully implemented in
                several key government institutions, including the Bangladesh
                Investment Development Authority (BIDA), Rajdhani Unnayan
                Kortipakha (RAJUK), and the Ministry of Housing and Public Works.
                As a result, document verification, decision-making, and
                administrative processes have become significantly faster and
                more efficient.
              </Text>
            </VStack>
            <Heading
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color="gray.800"
            >
              Why{" "}
              <Text as="span" color="#000B8C">
                Digitization Matters
              </Text>
            </Heading>
            <Box w="full">
              <img
                src="/png/Digitization.png"
                alt="Application Migration Services"
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "12px",
                  objectFit: "cover",
                }}
                loading="lazy"
              />
            </Box>

            {/* Why Choose Us Statistics Section */}
            <Box w="full" py={12}>
              <VStack spacing={8} align="left" w="full">
                <Heading
                  fontSize={{ base: "2xl", md: "3xl" }}
                  fontWeight="bold"
                  color="#000B8C"
                  textAlign="left"
                  align="left"
                >
                  Why Choose Us
                </Heading>

                <Grid
                  templateColumns={{
                    base: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(4, 1fr)",
                  }}
                  gap={8}
                  w="full"
                  maxW="6xl"
                >
                  {statisticsData.map((stat) => (
                    <Box
                      key={stat.id}
                      // p={6}
                      // borderRadius="lg"
                      borderLeft="2px solid"
                      borderLeftColor="#000B8C"
                      textAlign="center"
                      _hover={{
                        transform: "translateY(-2px)",
                        transition: "all 0.2s",
                      }}
                    >
                      <Box mb={4}>
                        <Box
                          w="60px"
                          h="60px"
                          mx="auto"
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          mb={4}
                        >
                          <img
                          src={stat.icon}
                          alt={`${stat.title} icon`}
                          width="60"
                          height="60"
                          loading="lazy"
                          style={{ display: "block" }}
                          />
                        </Box>
                      </Box>
                      <Text
                        fontSize={{ base: "2xl", md: "5xl" }}
                        fontWeight="normal"
                        color="#000B8C"
                        mb={2}
                      >
                        {stat.number}
                      </Text>
                      <Text fontSize="md" color="gray.600" fontWeight="medium">
                        {stat.title}
                      </Text>
                    </Box>
                  ))}
                </Grid>
              </VStack>
            </Box>

            {/* Specialized Services Section */}
            <Box w="full" py={8}>
              <VStack spacing={8} align="start" w="full">
                <Heading
                  fontSize={{ base: "2xl", md: "3xl" }}
                  fontWeight="bold"
                  color="gray.800"
                >
                  Our{" "}
                  <Text as="span" color="#000B8C">
                    Specialized Services
                  </Text>
                </Heading>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  maxW="4xl"
                >
                  We handle complex digitization jobs with:
                </Text>

                <Grid
                  templateColumns={{
                    base: "1fr",
                    md: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap={6}
                  w="full"
                >
                  {specializedServices1.map((service) => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </Grid>

                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.600"
                  maxW="4xl"
                >
                  Indexing & Metadata Tagging
                </Text>

                <Grid
                  templateColumns={{
                    base: "1fr",
                    md: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap={6}
                  w="full"
                >
                  {specializedServices2.map((service) => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </Grid>
              </VStack>
            </Box>

            {/* System Integration Section */}
            <CaseStudySection
              title="System Integration"
              subtitle=""
              features={systemIntegrationFeatures}
              bgColor="gray.50"
            />
            <VStack spacing={6} align="start" w="full">
              <Grid
                templateColumns={{
                  base: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                }}
                gap={6}
                w="full"
              >
                {["/png/rd_1.png", "/png/rd_2.png", "/png/rd_3.png"].map(
                  (src, idx) => (
                    <Box
                      key={idx}
                      overflow="hidden"
                      borderRadius="12px"
                      boxShadow="sm"
                      _hover={{
                        transform: "translateY(-2px)",
                        transition: "all 0.2s",
                      }}
                    >
                      <img
                        src={src}
                        alt={`Record digitization gallery ${idx + 1}`}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "220px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  )
                )}
              </Grid>
            </VStack>

            {/* Tools & Technologies Used Section */}
            <Box w="full">
              <VStack align="start" w="full">
                <Text
                  fontSize={{ base: "2xl", md: "3xl" }}
                  fontWeight="bold"
                  color="#000B8C"
                >
                  Tools & Technologies Used
                </Text>
                <ServicesGrid services={toolsAndTechnologies} />
              </VStack>
            </Box>

            {/* Proven Experience Section */}
            {/* <Box w="full" py={4}>
              <VStack spacing={2} align="start" w="full">
                <Heading
                  fontSize={{ base: "2xl", md: "3xl" }}
                  fontWeight="bold"
                  color="gray.800"
                >
                  Proven Experience{" "}
                  <Text as="span" color="#000B8C">
                    Across Sectors
                  </Text>
                </Heading>

                <VStack spacing={4} align="start" w="full">
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="gray.600"
                    lineHeight="1.2"
                    textAlign="justify"
                  >
                    <Text as="span" fontWeight="bold" color="gray.800">
                      Urban Development Authorities:
                    </Text>{" "}
                    We digitized complex land and housing records, including plot allocation, transfers, lease renewals, and mutation data—enabling fully automated citizen services.
                  </Text>
                  
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="gray.600"
                    lineHeight="1.2"
                    textAlign="justify"
                  >
                    <Text as="span" fontWeight="bold" color="gray.800">
                      Investment Development Authorities:
                    </Text>{" "}
                    Millions of company registration files were scanned, indexed, and archived—centralizing decades of data for policy, service delivery, and investor due diligence.
                  </Text>
                </VStack>
              </VStack>
            </Box> */}
          </VStack>
          <ExploreSection />
        </Container>
      </Box>
    </>
  );
};

export default RecordDigitizationServicesPage;
